/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Animated,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  isWeb,
  isNative,
  isAndroid,
  isIos,
  isMobileWeb,
} from '@global/screens';
import { parseDate, hasNoAccess, downloadFile } from '@global/helpers';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import * as Layout from '@components/screen/Layout';
import ModalFull from '@components/modal/ModalFull';
import StatusBadge from '@components/global/StatusBadge';
import DatePicker from '@components/form/DatePicker';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import CustomerSearch from '@components/form/search/CustomerReturnSearch';
import DeliveryAddressSearch from '../../components/form/search/DeliveryAddressSearch';
import LoaderTotal from '@/screens/sales-activity/components/LoaderTotal';
import SalesActivityDetail from '@/screens/sales-activity/components/SalesActivityDetail';
import CreateNewPlan from '@/screens/sales-activity/components/CreateNewPlan';
import SelectNewPlanRole from '@/screens/sales-activity/components/SelectNewPlanRole';
import SalesActivityForm from '@/screens/sales-activity/components/SalesActivityForm';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import store from '@/store';
import {
  filterAdded,
  paramsReseted,
  paginationAdded,
  sortChanged,
} from '@/store/params';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { logEventScreen } from '@global/analytics';
import Toast from 'react-native-toast-message';

const dimension = Dimensions.get('window');
const { height: windowHeight } = Dimensions.get('window');

const extractIds = (arr) =>
  Array.isArray(arr) ? arr.map((a) => a.id).filter(Boolean) : [];

const Skeleton = ({ width, height, style }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => {
      animation.stop();
    };
  }, [animatedValue]);

  const shimmerTranslateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-400, 400],
  });

  const opacity = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.4, 0.8, 0.4],
  });

  return (
    <View
      style={[
        {
          width,
          height,
          borderRadius: 4,
          backgroundColor: Colors.grey0,
          overflow: 'hidden',
        },
        style,
      ]}>
      <Animated.View
        style={{
          flex: 1,
          backgroundColor: Colors.grey0,
          opacity,
        }}
      />
      <Animated.View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transform: [{ translateX: shimmerTranslateX }],
        }}>
        <LinearGradient
          colors={['transparent', 'rgba(255,255,255,0.6)', 'transparent']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{
            flex: 1,
            width: 400,
          }}
        />
      </Animated.View>
    </View>
  );
};

const ActivityListSkeleton = () => {
  return (
    <View style={{ width: '100%' }}>
      {[1, 2, 3].map((dateIdx) => (
        <View key={dateIdx}>
          <View
            style={{
              width: '100%',
              paddingHorizontal: isWeb ? 15 : 0,
              paddingVertical: 15,
            }}>
            <View
              style={{ paddingHorizontal: isWeb ? 0 : 15, marginBottom: 15 }}>
              <Skeleton
                width={isWeb ? 220 : 200}
                height={18}
                style={{ borderRadius: 4 }}
              />
            </View>
            {[1, 2, 3].map((itemIdx) => (
              <View
                key={itemIdx}
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingHorizontal: isWeb ? 10 : 15,
                  paddingVertical: 15,
                  borderBottomWidth: 1,
                  borderBottomColor: Colors.grey0,
                }}>
                <View
                  style={{
                    flexDirection: isWeb ? 'row' : 'column',
                    alignItems: 'flex-start',
                    width: !isNative
                      ? 'calc(100% - 155px)'
                      : dimension.width - 170,
                  }}>
                  <View
                    style={{
                      width: isWeb ? 90 : '100%',
                      marginBottom: isWeb ? 0 : 8,
                    }}>
                    <Skeleton
                      width={isWeb ? 85 : 75}
                      height={13}
                      style={{ borderRadius: 4 }}
                    />
                  </View>
                  <View
                    style={{
                      flex: 1,
                      paddingHorizontal: isWeb ? 15 : 0,
                      marginTop: isWeb ? 0 : 0,
                    }}>
                    <Skeleton
                      width={isWeb ? '90%' : '95%'}
                      height={isWeb ? 14 : 13}
                      style={{ borderRadius: 4, marginBottom: 6 }}
                    />
                    <Skeleton
                      width={isWeb ? '65%' : '75%'}
                      height={11}
                      style={{ borderRadius: 4 }}
                    />
                  </View>
                </View>
                <View style={{ width: 155, alignItems: 'flex-end' }}>
                  <Skeleton
                    width={isWeb ? 85 : 70}
                    height={26}
                    style={{ borderRadius: 13 }}
                  />
                </View>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

const activityTypes = {
  planned_activities: ['draft', 'waiting_confirmation', 'rejected'],
  overdue_activities: ['draft', 'approved', 'waiting_confirmation'],
  past_activities: ['cancelled', 'finished'],
  upcoming_activities: ['approved'],
};

// Activity titles are persisted server-side as "<English prefix> - <customer name>"
// (see SalesActivityForm.js prefix_title), so translate() can never match the whole
// string. Split off the known prefix so SociollaText can translate just that part.
const ACTIVITY_TITLE_PREFIXES = [
  'Invoice Collection',
  'Engage Potential Customer',
  'Event Activity',
  'Visit',
];

const splitActivityTitle = (title) => {
  const matched = ACTIVITY_TITLE_PREFIXES.find(
    (prefix) => title === prefix || title.startsWith(`${prefix} - `),
  );
  return matched ? [matched, title.slice(matched.length)] : title;
};

const SalesActivityListScreen = (props) => {
  const { route, navigation } = props;
  const random_date = new Date(1970, 1, 1);

  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);
  const showPastExport = useSelector((state) => state.modal.showPastExport);
  const showGWPTracker = useSelector((state) => state.modal.showGWPTracker);
  const exportActivityOption = useSelector(
    (state) => state.modal.exportActivityOption,
  );
  const showBulkDeleteActivity = useSelector(
    (state) => state.modal.showBulkDeleteActivity,
  );
  const userData = useSelector((state) => state.auth.user);
  const userRole = userData && userData.role && userData.role.code;

  const [isReady, setIsReady] = useState(false);
  const [isLoadingTotal, setIsLoadingTotal] = useState(false);
  const [loadingPagination, setLoadingPagination] = useState(false);
  const [hidePagination, setHidePagination] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailId, setDetailId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentType, setCurrentType] = useState(null);
  const [showSelectRole, setShowSelectRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [activityType, setActivityType] = useState(
    'visiting_existing_customer',
  );
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSalesPerson, setFilterSalesPerson] = useState('');
  const [filterBeautyAdvisorPerson, setFilterBeautyAdvisorPerson] =
    useState('');
  const [filterMerchandiserPerson, setFilterMerchandiserPerson] = useState('');
  const [filterPromotorPerson, setFilterPromotorPerson] = useState('');
  const [filterCollectorPerson, setFilterCollectorPerson] = useState('');
  const [filterDeliveryAddress, setFilterDeliveryAddress] = useState([]);
  const [plannedActivityTab, setPlannedActivityTab] = useState('dated');
  const [filterExport, setFilterExport] = useState({
    sales_team: [],
    sales_person: [],
    beauty_advisor: [],
    merchandiser: [],
    promotor: [],
    collector: [],
    customer: [{ id: 'all', name: 'All' }],
    visited_at: {
      $gte: '',
      $lte: '',
    },
  });
  const [filterDelete, setFilterDelete] = useState({
    sales_team: [],
    sales_person: [],
    beauty_advisor: [],
    merchandiser: [],
    user_ids: [{ id: 'all', name: 'All' }],
    customer_ids: [{ id: 'add', name: 'All' }],
    visited_at: {
      $gte: '',
      $lte: '',
    },
  });
  const [totalPlanned, setTotalPlanned] = useState(0);
  const [totalOverdue, setTotalOverdue] = useState(0);
  const [totalPast, setTotalPast] = useState(0);
  const [activityObj, setActivityObj] = useState({});
  const [activityData, setActivityData] = useState([]);
  const [activityArrData, setActivityArrData] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [filterSalesTeamChanged, setFilterSalesTeamChanged] = useState(false);
  const [filterSalesPersonChanged, setFilterSalesPersonChanged] =
    useState(false);

  const showNewPlan = !hasNoAccess([
    'salesman',
    'sales_supervisor',
    'branch_head',
    'superadmin',
    'general',
  ]);

  const getActivityUserTag = (activity) => {
    const roleCode = activity?.user?.role;
    const userName = activity?.user?.name;
    if (!roleCode || !userName) {
      return '';
    }

    const roleLabels = {
      salesman: 'Salesman',
      beauty_advisor: 'Beauty Advisor',
      merchandiser: 'Merchandiser',
      promotor: 'Promotor',
      collector: 'Collector',
    };

    const formattedRole = roleLabels[roleCode] || '';
    return `${formattedRole} - ${userName}`;
  };

  const formatCheckTime = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return parseDate(date, 'HH:mm');
  };

  const getCheckInOutText = (activity) => {
    const checkIn = formatCheckTime(activity?.checkin_at) || '--:--';
    const checkOut = formatCheckTime(activity?.finished_at) || '--:--';

    return {
      label: 'Check In - Out',
      time: `${checkIn} - ${checkOut}`,
    };
  };

  const SALESTEAM_FILTER = useMemo(() => {
    const filter = {};
    const userSalesTeam = extractIds(userData?.sales_team);
    if (
      ![
        'superadmin',
        'distributor_manager',
        'general_manager',
        'vice_president',
        'national_key_account_manager',
        'tax_support',
        'general',
        'regional_manager',
        'group_key_account_manager',
        'regional_support',
        'key_account_manager',
      ].includes(userRole)
    ) {
      if (
        userSalesTeam.length &&
        !hasNoAccess([
          'branch_head',
          'salesman',
          'sales_supervisor',
          'regional_manager',
          'regional_support',
          'key_account_manager',
          'group_key_account_manager',
          'sales_admin',
          'beauty_advisor',
          'merchandiser',
          'regional_distributor_manager',
          'collector',
        ])
      ) {
        filter._id = { $in: userSalesTeam };
      } else {
        filter.empty = true;
      }
    }
    return filter;
  }, [userData, userRole]);

  const PROMOTOR_FILTER = useMemo(() => {
    return {
      remove_default_filter: true,
      'role.code': 'promotor',
    };
  }, []);

  const SALESPERSON_FILTER = useMemo(
    () =>
      (role = 'salesman', onlyfilter = false) => {
        const filter = {};
        let userSalesTeam = extractIds(userData?.sales_team);
        if (showPastExport || showGWPTracker) {
          userSalesTeam = extractIds(filterExport?.sales_team);
        }
        if (userSalesTeam?.length) {
          if (
            showPastExport ||
            showGWPTracker ||
            userRole == 'sales_supervisor'
          ) {
            if (userSalesTeam[0] !== 'all') {
              filter.sales_team = {
                $elemMatch: { id: { $in: userSalesTeam } },
              };
            } else {
              filter['role.code'] = role;
              if (role === 'salesman') {
                filter.sales_code = { $exists: true };
              }
              filter.remove_default_filter = true;
            }
            if (role != 'salesman') {
              filter['role.code'] = role;
              filter.remove_default_filter = true;
            }
          } else if (
            ['branch_head', 'finance_supervisor', 'sales_admin'].includes(
              userRole,
            )
          ) {
            filter.remove_default_filter = true;
            if (role != 'salesman') {
              filter['role.code'] = role;
              if (userSalesTeam[0] !== 'all') {
                filter.sales_team = {
                  $elemMatch: { id: { $in: userSalesTeam } },
                };
              } else {
                filter['role.code'] = role;
                filter.sales_code = { $exists: true };
                filter.remove_default_filter = true;
              }
            } else {
              filter.$and = [
                {
                  $or: [
                    { 'role.code': 'sales_supervisor' },
                    {
                      $and: [
                        { 'role.code': 'salesman' },
                        { sales_code: { $exists: true } },
                      ],
                    },
                  ],
                },
                { sales_team: { $elemMatch: { id: { $in: userSalesTeam } } } },
              ];
            }
          }
        } else if (
          !(showPastExport || showGWPTracker)
            ? !['superadmin', 'general', 'general_manager'].includes(userRole)
            : true
        ) {
          const INTERNAL_PIC_ROLES = [
            'regional_manager',
            'group_key_account_manager',
            'regional_support',
            'key_account_manager',
          ];
          if (
            (showPastExport || showGWPTracker) &&
            INTERNAL_PIC_ROLES.includes(userRole) &&
            role !== 'salesman'
          ) {
            filter.remove_default_filter = true;
            filter['role.code'] = role;
          } else {
            filter.empty = true;
          }
        }

        if (
          onlyfilter &&
          ['beauty_advisor', 'merchandiser', 'promotor', 'collector'].includes(
            role,
          )
        ) {
          filter.remove_default_filter = true;
          filter['role.code'] = role;
        }
        if (showGWPTracker) {
          filter['role.code'] = role;
        }
        return filter;
      },
    [userData, userRole, showPastExport, filterExport, showGWPTracker],
  );

  const CUSTOMER_FILTER = useMemo(() => {
    const filter = {};
    filter.status = 'approved';
    // [B2B-5551] Internal PIC should see all customers in export — removed assigned-customer restriction
    // const KAM_PIC_ROLES = ['regional_manager', 'group_key_account_manager'];
    // const AM_PIC_ROLES = ['regional_support', 'key_account_manager'];
    // if (KAM_PIC_ROLES.includes(userRole)) {
    //   filter['key_account_manager.id'] = { $in: [userData?._id] };
    // } else if (AM_PIC_ROLES.includes(userRole)) {
    //   filter['account_manager.id'] = { $in: [userData?._id] };
    // }
    const userSalesPerson = filterExport?.sales_person?.length
      ? filterExport.sales_person.map((a) => a.id)
      : [];
    const userBeautyAdvisor = filterExport?.beauty_advisor?.length
      ? filterExport.beauty_advisor.map((a) => a.id)
      : [];
    const userMerchandier = filterExport?.merchandiser?.length
      ? filterExport.merchandiser.map((a) => a.id)
      : [];
    if (userSalesPerson?.length && userSalesPerson[0] !== 'all') {
      filter['user.id'] = { $in: userSalesPerson };
    } else if (userBeautyAdvisor?.length && userBeautyAdvisor[0] !== 'all') {
      filter['user.id'] = { $in: userBeautyAdvisor };
    } else if (userMerchandier?.length && userMerchandier[0] !== 'all') {
      filter['user.id'] = { $in: userMerchandier };
    }
    return filter;
  }, [filterExport]);

  const getActivities = useApi(MsB2b.getActivities);

  const doFetchTotal = async () => {
    setIsLoadingTotal(true);
    try {
      const keys = Object.keys(activityTypes);
      const doChangeTotal = (act, total = 0) => {
        if (act == 'planned_activities') {
          setTotalPlanned(total);
        } else if (act == 'overdue_activities') {
          setTotalOverdue(total);
        } else if (act == 'past_activities') {
          setTotalPast(total);
        }
      };
      const countResponse = await MsB2b.getActivitiesCountAll();
      if (
        countResponse?.data?.data &&
        Object.keys(countResponse.data.data).length
      ) {
        for (let i = 0; i < keys.length; i++) {
          const act = keys[i];
          doChangeTotal(act, countResponse.data.data[act]);
        }
      } else {
        for (let i = 0; i < keys.length; i++) {
          const act = keys[i];
          doChangeTotal(act, 0);
        }
      }
      setTimeout(() => {
        setIsLoadingTotal(false);
      }, 250);
    } catch (e) {
      setIsLoadingTotal(false);
    }
  };

  const convertParams = (tabOverride = null) => {
    const parm = JSON.parse(JSON.stringify(store.getState().params));
    if (parm.filter['user.type'] === 'merchandiser') {
      delete parm.filter['user.type'];
    }
    // [B2B-5923] Keep 'user.type' for promotor. When the filter is "All"
    // (user.id $in ['all']), the API filters by user.role = user.type, so
    // deleting it makes the query match only old activities without a role.
    const params = {
      params: {
        ...parm,
        sort: '-visited_at',
      },
    };
    if (props?.route?.params?.type && activityTypes[props.route.params.type]) {
      const act_type = props.route.params.type;
      if (act_type == 'upcoming_activities') {
        params.params.filter.type = 'upcoming_activities';
        params.params.sort = 'visited_at';
      }
      if (act_type == 'planned_activities') {
        params.params.filter.type = 'planned_activities';
        params.params.sort = '-visited_at';
        const tab = tabOverride !== null ? tabOverride : plannedActivityTab;
        if (tab) {
          params.params.filter.planned_activity_tab = tab;
        }
      }
      if (act_type == 'overdue_activities') {
        params.params.filter.type = 'overdue_activities';
      }
      if (act_type == 'past_activities') {
        params.params.filter.type = 'past_activities';
      }
    }
    return params;
  };

  const convertData = (res) => {
    const obj = {};
    for (let i = 0; i < res.length; i++) {
      const item = res[i];
      const date = item.visited_at ? new Date(item.visited_at) : random_date;
      if (props?.route?.params?.type == 'overdue_activities') {
        item.status = 'overdue';
      }
      if (date) {
        const current_date = parseDate(date, 'yyyy-LL-dd');
        if (obj[current_date] && Array.isArray(obj[current_date])) {
          obj[current_date].push(item);
        } else {
          obj[current_date] = [item];
        }
      }
    }
    return obj;
  };

  const doSearch = async (loadMore = false, tabOverride = null) => {
    if (!loadMore) {
      dispatch(paginationAdded({ skip: 0, limit: isWeb ? 10 : 15 }));
    } else {
      setLoadingPagination(true);
    }
    const params = convertParams(tabOverride);
    const response = await getActivities.request(params);
    if (response?.data?.data?.length) {
      const new_response = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      const act_type = props?.route?.params?.type;

      if (new_response && new_response.length) {
        let obj = {};
        let arr = [];
        if (loadMore) {
          const arrData = activityArrData.concat(new_response).sort((a, b) => {
            if (act_type == 'upcoming_activities') {
              return new Date(a.visited_at) - new Date(b.visited_at);
            } else {
              return new Date(b.visited_at) - new Date(a.visited_at);
            }
          });
          setActivityArrData(arrData);

          obj = convertData(arrData);
          arr = Object.keys(obj);
        } else {
          setActivityArrData(new_response);

          obj = convertData(new_response);
          arr = Object.keys(obj);
        }

        setActivityObj(obj);
        setActivityData(arr);
      }

      setIsReady(true);
      setLoadingPagination(false);
    } else {
      if (!loadMore) {
        setActivityObj({});
        setActivityData([]);
        setActivityArrData([]);
        setIsReady(true);
      }

      setLoadingPagination(false);
      setHidePagination(true);
    }
  };

  const doLoadMore = () => {
    setLoadingPagination(true);
    dispatch(
      paginationAdded({
        skip: paramsState.skip + paramsState.limit,
        limit: paramsState.limit,
      }),
    );
    doSearch(true);
  };

  const resetData = () => {
    setActivityType('visiting_existing_customer');
    setDetailId('');
    setActivityObj({});
    setActivityData([]);
    setActivityArrData([]);
    setHidePagination(false);
    setTotalPlanned(0);
    setTotalOverdue(0);
    setTotalPast(0);
    setFilterCustomer('');
    setFilterSalesPerson('');
    setFilterBeautyAdvisorPerson('');
    setFilterMerchandiserPerson('');
    setFilterPromotorPerson('');
    setFilterCollectorPerson('');
    setFilterDeliveryAddress([]);
    setFilterExport({
      sales_team: [],
      sales_person: [],
      beauty_advisor: [],
      merchandiser: [],
      customer: [{ id: 'all', name: 'All' }],
      visited_at: {
        $gte: '',
        $lte: '',
      },
    });
    setFilterDelete({
      sales_team: [],
      sales_person: [],
      beauty_advisor: [],
      merchandiser: [],
      user_ids: [{ id: 'all', name: 'All' }],
      customer_ids: [{ id: 'all', name: 'All' }],
      visited_at: {
        $gte: '',
        $lte: '',
      },
    });
    dispatch(paramsReseted());
    dispatch(sortChanged(''));
    dispatch(showPopup({ name: 'showFilter', value: false }));
  };

  const openForm = (val) => {
    setActivityType(val);
    setShowCreate(false);
    setShowSelectRole(false);
    setShowForm(true);
  };

  const handleDetail = (id = '', shouldRefresh = false) => {
    if (id) {
      logEventScreen('SalesActivityDetailScreen');
      setDetailId(id);
      setTimeout(() => {
        setShowDetail(id ? true : false);
      }, 250);
    } else {
      if (detailId && showDetail) {
        setDetailId('');
        setShowDetail(false);
        props.navigation.setParams({ id: '', direct: '' });
      }
      if (shouldRefresh) {
        doFetchTotal();
        doSearch();
      }
    }
  };

  const onBulkDelete = async () => {
    setRemoving(true);
    try {
      if (!filterDelete.customer_ids?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter customer harus diisi',
            isConfirm: false,
            testID: 'sales-activity-bulk-delete-no-customer',
          }),
        );
        setRemoving(false);
        return;
      }
      if (!filterDelete.user_ids?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter user harus diisi',
            isConfirm: false,
            testID: 'sales-activity-bulk-delete-no-user',
          }),
        );
        setRemoving(false);
        return;
      }
      if (!filterDelete.visited_at?.$gte || !filterDelete.visited_at?.$lte) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date harus diisi',
            isConfirm: false,
            testID: 'sales-activity-bulk-delete-no-date',
          }),
        );
        setRemoving(false);
        return;
      }

      const payload = {
        user_ids: filterDelete.user_ids.map((a) => a.id),
        customer_ids: filterDelete.customer_ids.map((a) => a.id),
        visited_at: {
          start: filterDelete.visited_at.$gte,
          end: filterDelete.visited_at.$lte,
        },
        is_overdue_activities: Boolean(
          props?.route?.params?.type == 'overdue_activities',
        ),
        is_planned_activities: Boolean(
          props?.route?.params?.type == 'planned_activities',
        ),
      };
      const deleteAct = await MsB2b.deleteActivity('bulk', { data: payload });
      if (deleteAct?.data?.data) {
        dispatch(
          showAlert({
            show: true,
            title: 'Information',
            description: 'Successfully deleted activity data',
            isConfirm: false,
            testID: 'sales-activity-bulk-delete-success',
          }),
        );
      } else {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'A system error occurred',
            isConfirm: false,
            testID: 'sales-activity-bulk-delete-error',
          }),
        );
      }
      setRemoving(false);
    } catch (error) {
      setRemoving(false);
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
          testID: 'sales-activity-bulk-delete-error',
        }),
      );
    }
  };

  useEffect(() => {
    let mounted = true;
    // addListener returns the unsubscribe fn. removeListener('focus') without the
    // callback matches nothing, so listeners piled up and every focus fired N
    // duplicate fetches whose responses raced each other.
    const unsubscribeFocus = props.navigation.addListener('focus', () => {
      if (props?.route?.params?.type) {
        setCurrentType(props.route.params.type);
      }
      setIsReady(false);
      resetData();
      setPlannedActivityTab('dated');
      dispatch(sortChanged('visited_at'));
      doFetchTotal();
      doSearch();
    });
    if (mounted && props?.route?.params?.type != currentType && isReady) {
      setCurrentType(props?.route?.params?.type);
      resetData();
      setPlannedActivityTab('dated');
      setTimeout(() => {
        if (mounted) {
          doFetchTotal();
          doSearch();
        }
      }, 500);
    }

    return () => {
      mounted = false;
      unsubscribeFocus();
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    if (mounted && props?.route?.params?.type) {
      const total_data = activityArrData.length;
      const parm = JSON.parse(JSON.stringify(store.getState().params));
      if (
        parm.skip >= total_data ||
        total_data < parm.limit ||
        total_data <= 0
      ) {
        setHidePagination(true);
      } else {
        setHidePagination(false);
      }
    }
    return () => {
      mounted = false;
    };
  }, [activityArrData, paramsState.skip]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setFilterSalesTeamChanged(true);
      setTimeout(() => {
        setFilterSalesTeamChanged(false);
      }, 500);
    }
    return () => {
      mounted = false;
    };
  }, [filterExport.sales_team]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setFilterSalesPersonChanged(true);
      setTimeout(() => {
        setFilterSalesPersonChanged(false);
      }, 500);
    }
    return () => {
      mounted = false;
    };
  }, [filterExport.sales_person]);

  const goToList = (type, title) => {
    props.navigation.replace('SalesActivityListScreen', {
      type: type,
      titleScreen: title,
    });
  };

  const goBack = () => {
    if (props?.route?.params?.isHome) {
      const { isHome, ...restParams } = props.route?.params || {};
      props.navigation.replace('SalesActivityListScreen', restParams);
      props.navigation.navigate('HomeNavigator');
    } else {
      props.navigation.replace('SalesActivityScreen');
    }
  };

  const exportPastActivity = async () => {
    setExporting(true);
    const userDetail = userData?.role;
    const userDetailRole = userData?.role?.code;
    try {
      if (!filterExport.customer?.length) {
        if (isIos) {
          Toast.show({
            text1: 'Customer filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Customer filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-export-no-customer',
            }),
          );
        }
        setExporting(false);
        return;
      }
      if (
        !filterExport.sales_person?.length &&
        !filterExport.beauty_advisor?.length &&
        !filterExport.merchandiser?.length &&
        !filterExport.promotor?.length &&
        !filterExport.collector?.length &&
        ![
          'salesman',
          'beauty_advisor',
          'merchandiser',
          'promotor',
          'collector',
        ].includes(userRole)
      ) {
        if (isIos) {
          Toast.show({
            text1: 'Sales person filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Sales person filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-export-no-sales-person',
            }),
          );
        }
        setExporting(false);
        return;
      }
      if (!filterExport.visited_at?.$gte || !filterExport.visited_at?.$lte) {
        if (isIos) {
          Toast.show({ text1: 'Date filter must be filled', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Date filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-export-no-date',
            }),
          );
        }
        setExporting(false);
        return;
      }

      let activity_type = '';
      const act = props?.route?.params?.type;
      if (act == 'past_activities') {
        activity_type = 'past';
      } else if (act == 'overdue_activities') {
        activity_type = 'overdue';
      }

      let filterRole = null;
      let filterRoleIDs = [];
      if (filterExport?.sales_person?.length > 0) {
        filterRole = filterExport.sales_person[0]?.role?.code || 'salesman';
        filterRoleIDs = (filterExport.sales_person || []).map((a) => a.id);
      } else if (filterExport?.beauty_advisor?.length > 0) {
        filterRole =
          filterExport.beauty_advisor[0]?.role?.code || 'beauty_advisor';
        filterRoleIDs = (filterExport.beauty_advisor || []).map((a) => a.id);
      } else if (filterExport?.merchandiser?.length > 0) {
        filterRole = filterExport.merchandiser[0]?.role?.code || 'merchandiser';
        filterRoleIDs = (filterExport.merchandiser || []).map((a) => a.id);
      } else if (filterExport?.promotor?.length > 0) {
        filterRole = filterExport.promotor[0]?.role?.code || 'promotor';
        filterRoleIDs = (filterExport.promotor || []).map((a) => a.id);
      } else if (filterExport?.collector?.length > 0) {
        filterRole = filterExport.collector[0]?.role?.code || 'collector';
        filterRoleIDs = (filterExport.collector || []).map((a) => a.id);
      }

      if (
        [
          'salesman',
          'beauty_advisor',
          'merchandiser',
          'promotor',
          'collector',
        ].includes(userDetailRole)
      ) {
        filterRole = userDetailRole;
        filterRoleIDs = userDetail.id;
      }

      const payload = {
        'sales_team.id': {
          $in: filterExport.sales_team.map((a) => a.id),
        },
        'customer.id': { $in: filterExport.customer.map((a) => a.id) },
        visited_at: filterExport.visited_at,
      };

      payload[`${filterRole}.id`] = { $in: filterRoleIDs };

      if (activity_type == 'overdue') {
        payload.finished_at = { $lte: new Date().toISOString() };
      }

      const response = await MsB2b.exportActivityReport(payload, {
        params: {
          filter: {
            activity_type,
          },
        },
      });
      if (response?.data?.success) {
        if (isIos) {
          Toast.show({
            text1: 'Export in progress, the result will be sent to your email.',
            type: 'success',
          });
          dispatch(
            showPopup({
              name: 'showPastExport',
              value: false,
            }),
          );
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Information',
              description:
                'Export in progress, the result will be sent to your email.',
              isConfirm: false,
              testID: 'sales-activity-export-success',
              onCallback: () => {
                dispatch(
                  showPopup({
                    name: 'showPastExport',
                    value: false,
                  }),
                );
              },
            }),
          );
        }
        downloadFile(response.data.data, 'past-activity-report');
      } else {
        if (isIos) {
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'A system error occurred',
              isConfirm: false,
              testID: 'sales-activity-export-error',
            }),
          );
        }
      }
      setExporting(false);
    } catch (error) {
      if (isIos) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } else {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description:
              error?.response?.data?.message || 'A system error occurred',
            isConfirm: false,
            testID: 'sales-activity-export-error',
          }),
        );
      }
      setExporting(false);
    }
  };

  const exportGWPTracker = async () => {
    setExporting(true);
    try {
      if (!filterExport.customer?.length) {
        if (isIos) {
          Toast.show({
            text1: 'Customer filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Customer filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-gwp-export-no-customer',
            }),
          );
        }
        setExporting(false);
        return;
      }
      if (
        !filterExport.beauty_advisor?.length &&
        !filterExport.promotor?.length &&
        !['beauty_advisor', 'promotor'].includes(userRole)
      ) {
        if (isIos) {
          Toast.show({
            text1: 'Beauty advisor or promotor filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Beauty advisor or promotor filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-gwp-export-no-ba',
            }),
          );
        }
        setExporting(false);
        return;
      }
      if (!filterExport.visited_at?.$gte || !filterExport.visited_at?.$lte) {
        if (isIos) {
          Toast.show({ text1: 'Date filter must be filled', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Date filter must be filled',
              isConfirm: false,
              testID: 'sales-activity-gwp-export-no-date',
            }),
          );
        }
        setExporting(false);
        return;
      }

      let activity_type = '';
      const act = props?.route?.params?.type;
      if (act == 'past_activities') {
        activity_type = 'past';
      } else if (act == 'overdue_activities') {
        activity_type = 'overdue';
      }

      const todo_type = 'gwp_tracker';

      let filterRoleIDs = [];
      let filterRoleKey = 'beauty_advisor';
      if (filterExport?.beauty_advisor?.length > 0) {
        filterRoleIDs = (filterExport.beauty_advisor || []).map((a) => a.id);
      } else if (filterExport?.promotor?.length > 0) {
        filterRoleIDs = (filterExport.promotor || []).map((a) => a.id);
        filterRoleKey = 'promotor';
      } else if (['beauty_advisor', 'promotor'].includes(userRole)) {
        filterRoleKey = userRole;
      }

      const payload = {
        'sales_team.id': {
          $in: filterExport.sales_team.map((a) => a.id),
        },
        'customer.id': { $in: filterExport.customer.map((a) => a.id) },
        visited_at: filterExport.visited_at,
      };

      payload[`${filterRoleKey}.id`] = { $in: filterRoleIDs };

      if (activity_type == 'overdue') {
        payload.finished_at = { $lte: new Date().toISOString() };
      }

      const response = await MsB2b.exportActivityReport(payload, {
        params: {
          filter: {
            activity_type,
            todo_type,
          },
        },
      });
      if (response?.data?.success) {
        if (isIos) {
          Toast.show({
            text1: 'Export in progress, the result will be sent to your email.',
            type: 'success',
          });
          dispatch(
            showPopup({
              name: 'showGWPTracker',
              value: false,
            }),
          );
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Information',
              description:
                'Export in progress, the result will be sent to your email.',
              isConfirm: false,
              testID: 'sales-activity-gwp-export-success',
              onCallback: () => {
                dispatch(
                  showPopup({
                    name: 'showGWPTracker',
                    value: false,
                  }),
                );
              },
            }),
          );
        }
        downloadFile(response.data.data, 'GWP-tracker-report');
      } else {
        if (isIos) {
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'A system error occurred',
              isConfirm: false,
              testID: 'sales-activity-gwp-export-error',
            }),
          );
        }
      }
      setExporting(false);
    } catch (error) {
      if (isIos) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } else {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description:
              error?.response?.data?.message || 'A system error occurred',
            isConfirm: false,
            testID: 'sales-activity-gwp-export-error',
          }),
        );
      }
      setExporting(false);
    }
  };

  let title_section = '';
  let type = 'default';
  let page_type = '';
  let cardBackground = `${Colors.blue0}10`;
  if (props.route && props.route.params) {
    if (props.route.params.titleScreen) {
      title_section = props.route.params.titleScreen;
    }
    if (props.route.params.type) {
      page_type = props.route.params.type;
      const activity = props.route.params.type;
      if (activity == 'planned_activities') {
        cardBackground = `${Colors.blue0}10`;
        type = 'primary';
      } else if (activity == 'overdue_activities') {
        cardBackground = `${Colors.red1}10`;
        type = 'danger';
      } else if (activity == 'past_activities') {
        cardBackground = `${Colors.yellow0}10`;
        type = 'warning';
      } else {
        cardBackground = `${Colors.green1}10`;
        type = 'success';
      }
    }
  }

  const gwpTracker = () => {
    dispatch(showPopup({ name: 'exportActivityOption', value: false }));
    dispatch(showPopup({ name: 'showGWPTracker', value: true }));
    setExporting(false);
    setFilterExport({
      sales_team: [],
      sales_person: [],
      beauty_advisor: [],
      promotor: [],
      customer: [{ id: 'all', name: 'All' }],
      visited_at: {
        $gte: '',
        $lte: '',
      },
    });
  };

  const pastActivity = () => {
    dispatch(showPopup({ name: 'exportActivityOption', value: false }));
    dispatch(showPopup({ name: 'showPastExport', value: true }));
    setExporting(false);
    setFilterExport({
      sales_team: [],
      sales_person: [],
      beauty_advisor: [],
      merchandiser: [],
      promotor: [],
      collector: [],
      customer: [{ id: 'all', name: 'All' }],
      visited_at: {
        $gte: '',
        $lte: '',
      },
    });
  };

  const removeAddressFilter = (filter = {}) => {
    const newFilter = JSON.parse(JSON.stringify(filter || {}));

    delete newFilter['customer.address.id'];
    delete newFilter['customer.address.type'];
    delete newFilter.customer_address_delivery_ids;
    delete newFilter.customer_address_store_ids;
    delete newFilter.customer_address_outlet_ids;

    return newFilter;
  };

  return (
    <>
      <Layout.Container testID="sales-activity-list-screen">
        {isWeb ? (
          <Layout.Back
            testID="sales-activity-list-back-btn"
            title={props?.route?.params?.isHome ? 'Home' : 'Sales Activity'}
            onCallback={() => goBack()}
          />
        ) : (
          false
        )}
        <View
          style={{
            width: '100%',
            maxWidth: 1024,
            flexDirection: isWeb ? 'row' : 'column',
            justifyContent: 'space-between',
            marginBottom: 100,
            paddingBottom: 100,
          }}>
          <Layout.Wrapper white={false}>
            <Layout.ScrollViewRefresher
              isFullHeight={!isMobileWeb}
              {...(!isMobileWeb ? {} : { maxHeight: windowHeight * 0.89 })}
              onCallback={() => {
                doFetchTotal();
                doSearch();
              }}>
              <Layout.Card
                type={type}
                style={{
                  padding: 0,
                  backgroundColor: Colors.white,
                  ...(!isWeb ? { borderTopColor: 'transparent' } : {}),
                  paddingBottom: isAndroid ? 80 : isIos ? 20 : 0,
                }}>
                {isWeb ? (
                  <View
                    style={{
                      width: '100%',
                      padding: 15,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: cardBackground,
                    }}>
                    <SociollaText type="medium" style={{ fontSize: 18 }}>
                      {title_section}
                    </SociollaText>
                    <View style={{ flexDirection: 'row' }}>
                      {!hasNoAccess(['superadmin', 'general']) &&
                      ['planned_activities', 'overdue_activities'].includes(
                        props?.route?.params?.type,
                      ) ? (
                        <View style={{ marginRight: 10 }}>
                          <Button
                            testID="sales-activity-list-delete-btn"
                            rounded={true}
                            type="danger"
                            buttonStyle={{ width: 90 }}
                            title={
                              <SociollaText
                                type="medium"
                                style={{ fontSize: 14, color: Colors.white }}>
                                Delete
                              </SociollaText>
                            }
                            onPress={() => {
                              setRemoving(false);
                              setFilterDelete({
                                user_ids: [{ id: 'all', name: 'All' }],
                                customer_ids: [{ id: 'all', name: 'All' }],
                                visited_at: {
                                  $gte: '',
                                  $lte: '',
                                },
                                is_overdue_activities: Boolean(
                                  props?.route?.params?.type ==
                                    'overdue_activities',
                                ),
                                is_planned_activities: Boolean(
                                  props?.route?.params?.type ==
                                    'planned_activities',
                                ),
                              });
                              dispatch(
                                showPopup({
                                  name: 'showBulkDeleteActivity',
                                  value: true,
                                }),
                              );
                            }}
                          />
                        </View>
                      ) : (
                        false
                      )}
                      {['overdue_activities', 'past_activities'].includes(
                        props?.route?.params?.type,
                      ) ? (
                        <View style={{ marginRight: 10 }}>
                          <Button
                            testID="sales-activity-list-export-btn"
                            rounded={true}
                            type="primary"
                            buttonStyle={{ width: 90 }}
                            title={
                              <SociollaText
                                type="medium"
                                style={{ fontSize: 14, color: Colors.white }}>
                                Export
                              </SociollaText>
                            }
                            onPress={() => {
                              dispatch(
                                showPopup({
                                  name: 'exportActivityOption',
                                  value: true,
                                }),
                              );
                            }}
                          />
                        </View>
                      ) : (
                        false
                      )}
                      <View>
                        <Button
                          testID="sales-activity-list-filter-btn"
                          rounded={true}
                          type="primary"
                          buttonStyle={{ width: 90 }}
                          title={
                            <View style={{ flexDirection: 'row' }}>
                              <Image
                                style={{
                                  width: 12,
                                  height: 15,
                                  marginRight: 10,
                                }}
                                width={12}
                                height={15}
                                svg={true}
                                source={require('@assets/img/icons/filter-white.svg')}
                              />
                              <SociollaText
                                type="medium"
                                style={{ fontSize: 14, color: Colors.white }}>
                                Filter
                              </SociollaText>
                            </View>
                          }
                          onPress={() =>
                            dispatch(
                              showPopup({
                                name: 'showFilter',
                                value: 'sales-activity',
                              }),
                            )
                          }
                        />
                      </View>
                    </View>
                  </View>
                ) : (
                  false
                )}
                {page_type == 'planned_activities' ? (
                  <View
                    style={{
                      width: '100%',
                      flexDirection: 'row',
                      borderBottomWidth: 1,
                      borderBottomColor: Colors.grey0,
                      paddingHorizontal: isWeb ? 15 : 0,
                    }}>
                    <TouchableOpacity
                      testID="sales-activity-list-tab-dated"
                      activeOpacity={0.6}
                      onPress={() => {
                        setActivityObj({});
                        setActivityData([]);
                        setActivityArrData([]);
                        setIsReady(false);
                        dispatch(
                          paginationAdded({ skip: 0, limit: isWeb ? 10 : 15 }),
                        );
                        setPlannedActivityTab('dated');
                        doSearch(false, 'dated');
                      }}
                      style={{
                        flex: 1,
                        paddingVertical: 15,
                        paddingHorizontal: 15,
                        borderBottomWidth:
                          plannedActivityTab === 'dated' ? 2 : 0,
                        borderBottomColor:
                          plannedActivityTab === 'dated'
                            ? Colors.blue0
                            : 'transparent',
                        alignItems: 'center',
                      }}>
                      <SociollaText
                        type="medium"
                        style={{
                          fontSize: 14,
                          color:
                            plannedActivityTab === 'dated'
                              ? Colors.blue0
                              : Colors.grey1,
                        }}>
                        Dated
                      </SociollaText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      testID="sales-activity-list-tab-no-date"
                      activeOpacity={0.6}
                      onPress={() => {
                        setActivityObj({});
                        setActivityData([]);
                        setActivityArrData([]);
                        setIsReady(false);
                        dispatch(
                          paginationAdded({ skip: 0, limit: isWeb ? 10 : 15 }),
                        );
                        setPlannedActivityTab('no_date');
                        doSearch(false, 'no_date');
                      }}
                      style={{
                        flex: 1,
                        paddingVertical: 15,
                        paddingHorizontal: 15,
                        borderBottomWidth:
                          plannedActivityTab === 'no_date' ? 2 : 0,
                        borderBottomColor:
                          plannedActivityTab === 'no_date'
                            ? Colors.blue0
                            : 'transparent',
                        alignItems: 'center',
                      }}>
                      <SociollaText
                        type="medium"
                        style={{
                          fontSize: 14,
                          color:
                            plannedActivityTab === 'no_date'
                              ? Colors.blue0
                              : Colors.grey1,
                        }}>
                        No Date
                      </SociollaText>
                    </TouchableOpacity>
                  </View>
                ) : (
                  false
                )}
                <View style={{ width: '100%' }}>
                  {!activityData.length && getActivities.loading ? (
                    <ActivityListSkeleton />
                  ) : activityData && activityData.length ? (
                    activityData.map((item, idx) => (
                      <View key={idx}>
                        <View
                          style={{
                            width: '100%',
                            paddingHorizontal: isWeb ? 15 : 0,
                            paddingVertical: 15,
                          }}>
                          <View style={{ paddingHorizontal: isWeb ? 0 : 15 }}>
                            <SociollaText
                              type="medium"
                              style={{ fontSize: 16 }}>
                              {parseDate(item) == parseDate(random_date)
                                ? 'No date'
                                : parseDate(
                                    new Date(item),
                                    'eee, dd MMMM yyyy',
                                  )}
                            </SociollaText>
                          </View>
                          {activityObj[item] &&
                          Object.keys(activityObj[item]).length ? (
                            <>
                              {activityObj[item] && activityObj[item].length
                                ? activityObj[item].map((child, childIdx) => {
                                    const userTag = getActivityUserTag(child);
                                    const checkInOutText =
                                      getCheckInOutText(child);
                                    const title =
                                      child?.title?.split(/\r?\n/)[0]?.trim() ||
                                      child?.title ||
                                      '';
                                    const address =
                                      child?.customer?.address?.name || '';
                                    const shouldShowAddress =
                                      child?.activity_type !==
                                        'engage_potential_customers' ||
                                      Boolean(address);

                                    return (
                                      <TouchableOpacity
                                        testID={`sales-activity-list-item-${idx}-${childIdx}`}
                                        onPress={() => handleDetail(child._id)}
                                        activeOpacity={0.6}
                                        style={{
                                          width: '100%',
                                          flexDirection: 'row',
                                          alignItems: 'flex-start',
                                          justifyContent: 'space-between',
                                          paddingHorizontal: isWeb ? 10 : 15,
                                          paddingVertical: 15,
                                          borderBottomWidth: 1,
                                          borderBottomColor: Colors.grey0,
                                        }}
                                        key={`${idx}-${childIdx}`}>
                                        <View
                                          style={{
                                            flexDirection: isWeb
                                              ? 'row'
                                              : 'column',
                                            alignItems: 'flex-start',
                                            width: !isNative
                                              ? 'calc(100% - 155px)'
                                              : dimension.width - 170,
                                          }}>
                                          <SociollaText
                                            style={{ color: Colors.grey1 }}>
                                            {child?.start_time &&
                                            child?.end_time
                                              ? `${child?.start_time} - ${child?.end_time}`
                                              : 'No time'}
                                          </SociollaText>
                                          <View
                                            style={{
                                              paddingHorizontal: isWeb ? 15 : 0,
                                              flexShrink: 1,
                                              marginTop: isWeb ? 3 : 0,
                                              gap: shouldShowAddress ? 3 : 0,
                                            }}>
                                            <SociollaText
                                              style={{
                                                fontSize: isWeb ? 13 : 12,
                                              }}>
                                              {splitActivityTitle(title)}
                                            </SociollaText>
                                            {shouldShowAddress ? (
                                              <SociollaText
                                                style={{
                                                  fontSize: isWeb ? 13 : 12,
                                                }}>
                                                {address}
                                              </SociollaText>
                                            ) : null}
                                            {userTag ? (
                                              <SociollaText
                                                style={styles.activityUserTag}>
                                                {userTag}
                                              </SociollaText>
                                            ) : null}
                                          </View>
                                        </View>
                                        <View
                                          style={{
                                            width: 155,
                                            alignItems: 'center',
                                            paddingRight: isWeb ? 0 : 10,
                                            paddingTop: 10,
                                          }}>
                                          <StatusBadge status={child.status} />
                                          {child?.status === 'finished' &&
                                          checkInOutText ? (
                                            <View
                                              style={{
                                                alignItems: 'center',
                                                marginTop: 8,
                                              }}>
                                              <SociollaText
                                                style={
                                                  styles.activityCheckLabel
                                                }>
                                                {checkInOutText.label}
                                              </SociollaText>
                                              <SociollaText
                                                style={
                                                  styles.activityCheckTime
                                                }>
                                                {checkInOutText.time}
                                              </SociollaText>
                                            </View>
                                          ) : null}
                                        </View>
                                      </TouchableOpacity>
                                    );
                                  })
                                : false}
                            </>
                          ) : (
                            false
                          )}
                        </View>
                      </View>
                    ))
                  ) : (
                    <View
                      style={{
                        width: '100%',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 20,
                      }}>
                      <SociollaText>No activity found</SociollaText>
                    </View>
                  )}
                  {!hidePagination && loadingPagination ? (
                    <Layout.Loader />
                  ) : hidePagination ? (
                    false
                  ) : (
                    <View
                      style={{
                        width: '100%',
                        padding: 15,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Button
                        testID="sales-activity-list-load-more-btn"
                        rounded={true}
                        type="primary"
                        buttonStyle={{
                          width: 'auto',
                          minWidth: 120,
                          paddingHorizontal: 16,
                        }}
                        title={
                          <SociollaText
                            type="medium"
                            style={{ fontSize: 14, color: Colors.white }}>
                            Load More
                          </SociollaText>
                        }
                        onPress={() => doLoadMore()}
                      />
                    </View>
                  )}
                </View>
              </Layout.Card>
            </Layout.ScrollViewRefresher>
          </Layout.Wrapper>
          {isWeb ? (
            <View style={{ width: '18%', maxWidth: 225 }}>
              {showNewPlan ? (
                <Button
                  testID="sales-activity-list-plan-new-btn"
                  rounded={true}
                  buttonStyle={{ width: '100%', marginBottom: 15 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Plan New Activity
                    </SociollaText>
                  }
                  onPress={() => {
                    logEventScreen('PlanNewActivityScreen');
                    if (
                      ![
                        'salesman',
                        'beauty_advisor',
                        'merchandiser',
                        'promotor',
                        'collector',
                      ].includes(userData?.role?.code)
                    ) {
                      setShowSelectRole(true);
                    } else {
                      setSelectedRole(userData?.role?.code);
                      setShowCreate(true);
                    }
                  }}
                />
              ) : (
                false
              )}
              {isWeb ? (
                <>
                  {page_type != 'planned_activities' ? (
                    <Layout.Card
                      testID="sales-activity-list-card-planned"
                      type="primary"
                      style={{ marginBottom: 15 }}
                      touchable
                      onPress={() =>
                        goToList('planned_activities', 'Planned Activities')
                      }>
                      {isLoadingTotal ? (
                        <LoaderTotal />
                      ) : (
                        <SociollaText type="medium" style={styles.cardTitle}>
                          {totalPlanned}
                        </SociollaText>
                      )}
                      <SociollaText type="medium" style={styles.cardDesc}>
                        Planned Activities
                      </SociollaText>
                    </Layout.Card>
                  ) : (
                    false
                  )}
                  {page_type != 'overdue_activities' ? (
                    <Layout.Card
                      testID="sales-activity-list-card-overdue"
                      type="danger"
                      style={{ marginBottom: 15 }}
                      touchable
                      onPress={() =>
                        goToList('overdue_activities', 'Overdue Activities')
                      }>
                      {isLoadingTotal ? (
                        <LoaderTotal />
                      ) : (
                        <SociollaText type="medium" style={styles.cardTitle}>
                          {totalOverdue}
                        </SociollaText>
                      )}
                      <SociollaText type="medium" style={styles.cardDesc}>
                        Overdue Activities
                      </SociollaText>
                    </Layout.Card>
                  ) : (
                    false
                  )}
                  {page_type != 'past_activities' ? (
                    <Layout.Card
                      testID="sales-activity-list-card-past"
                      type="warning"
                      touchable
                      onPress={() =>
                        goToList('past_activities', 'Past Activities')
                      }>
                      {isLoadingTotal ? (
                        <LoaderTotal />
                      ) : (
                        <SociollaText type="medium" style={styles.cardTitle}>
                          {totalPast}
                        </SociollaText>
                      )}
                      <SociollaText type="medium" style={styles.cardDesc}>
                        Past Activities
                      </SociollaText>
                    </Layout.Card>
                  ) : (
                    false
                  )}
                </>
              ) : (
                false
              )}
            </View>
          ) : (
            false
          )}
        </View>
        <Layout.Filter
          testID="sales-activity-list-filter"
          name="sales-activity"
          onReset={() => {
            setFilterCustomer('');
            setFilterSalesPerson('');
            setFilterBeautyAdvisorPerson('');
            setFilterMerchandiserPerson('');
            setFilterPromotorPerson('');
            setFilterCollectorPerson('');
            setFilterDeliveryAddress([]);

            const oldFilter = JSON.parse(JSON.stringify(paramsState.filter));
            delete oldFilter['customer.id'];
            delete oldFilter['user.id'];
            delete oldFilter['user.type'];
            delete oldFilter.visited_at;

            dispatch(filterAdded(removeAddressFilter(oldFilter)));
            doSearch();
          }}
          onApply={() => {
            doSearch();
            dispatch(showPopup({ name: 'showFilter', value: false }));
          }}>
          {![
            'salesman',
            'beauty_advisor',
            'merchandiser',
            'promotor',
            'collector',
          ].includes(userRole) ? (
            <>
              <Layout.FilterItem>
                <SalesPersonSearch
                  testID="sales-activity-list-filter-sales-person"
                  label="Sales Person"
                  value={filterSalesPerson}
                  multiple={true}
                  customFilter={SALESPERSON_FILTER('salesman', true)}
                  isAll
                  onChange={(value) => {
                    setFilterSalesPerson(value);
                    if (value && value.length) {
                      dispatch(
                        filterAdded({
                          ...paramsState.filter,
                          'user.id': { $in: value.map((a) => a.id) },
                          'user.type': 'salesman',
                        }),
                      );
                    } else {
                      setFilterSalesPerson('');
                      const oldFilter = JSON.parse(
                        JSON.stringify(paramsState.filter),
                      );
                      delete oldFilter['user.id'];
                      delete oldFilter['user.type'];
                      dispatch(filterAdded(oldFilter));
                    }
                  }}
                  editable={
                    !paramsState?.filter?.['user.type'] ||
                    paramsState?.filter?.['user.type'] == 'salesman'
                  }
                />
              </Layout.FilterItem>
              <Layout.FilterItem>
                <SalesPersonSearch
                  testID="sales-activity-list-filter-beauty-advisor"
                  label="Beauty Advisor"
                  value={filterBeautyAdvisorPerson}
                  multiple={true}
                  customFilter={SALESPERSON_FILTER('beauty_advisor', true)}
                  isAll
                  onChange={(value) => {
                    setFilterBeautyAdvisorPerson(value);
                    if (value && value.length) {
                      dispatch(
                        filterAdded({
                          ...paramsState.filter,
                          'user.id': {
                            $in: value.map((a) => a.id),
                          },
                          'user.type': 'beauty_advisor',
                        }),
                      );
                    } else {
                      setFilterBeautyAdvisorPerson('');
                      const oldFilter = JSON.parse(
                        JSON.stringify(paramsState.filter),
                      );
                      delete oldFilter['user.id'];
                      delete oldFilter['user.type'];
                      dispatch(filterAdded(oldFilter));
                    }
                  }}
                  editable={
                    !paramsState?.filter?.['user.type'] ||
                    paramsState?.filter?.['user.type'] == 'beauty_advisor'
                  }
                />
              </Layout.FilterItem>
              <Layout.FilterItem>
                <SalesPersonSearch
                  testID="sales-activity-list-filter-merchandiser"
                  label="Merchandiser"
                  value={filterMerchandiserPerson}
                  multiple={true}
                  customFilter={SALESPERSON_FILTER('merchandiser', true)}
                  onChange={(value) => {
                    setFilterMerchandiserPerson(value);
                    if (value && value.length) {
                      dispatch(
                        filterAdded({
                          ...paramsState.filter,
                          'user.id': {
                            $in: value.map((a) => a.id),
                          },
                          'user.type': 'merchandiser',
                        }),
                      );
                    } else {
                      setFilterMerchandiserPerson('');
                      const oldFilter = JSON.parse(
                        JSON.stringify(paramsState.filter),
                      );
                      delete oldFilter['user.id'];
                      delete oldFilter['user.type'];
                      dispatch(filterAdded(oldFilter));
                    }
                  }}
                  editable={
                    !paramsState?.filter?.['user.type'] ||
                    paramsState?.filter?.['user.type'] == 'merchandiser'
                  }
                />
              </Layout.FilterItem>
              <Layout.FilterItem>
                <SalesPersonSearch
                  testID="sales-activity-list-filter-promotor"
                  label="Promotor"
                  value={filterPromotorPerson}
                  multiple={true}
                  isAll
                  customFilter={PROMOTOR_FILTER}
                  onChange={(value) => {
                    setFilterPromotorPerson(value);
                    if (value && value.length) {
                      dispatch(
                        filterAdded({
                          ...paramsState.filter,
                          'user.id': {
                            $in: value.map((a) => a.id),
                          },
                          'user.type': 'promotor',
                        }),
                      );
                    } else {
                      setFilterPromotorPerson('');
                      const oldFilter = JSON.parse(
                        JSON.stringify(paramsState.filter),
                      );
                      delete oldFilter['user.id'];
                      delete oldFilter['user.type'];
                      dispatch(filterAdded(oldFilter));
                    }
                  }}
                  editable={
                    !paramsState?.filter?.['user.type'] ||
                    paramsState?.filter?.['user.type'] == 'promotor'
                  }
                />
              </Layout.FilterItem>
              <Layout.FilterItem>
                <SalesPersonSearch
                  testID="sales-activity-list-filter-collector"
                  label="Collector"
                  value={filterCollectorPerson}
                  multiple={true}
                  customFilter={SALESPERSON_FILTER('collector', true)}
                  isAll
                  onChange={(value) => {
                    setFilterCollectorPerson(value);
                    if (value && value.length) {
                      dispatch(
                        filterAdded({
                          ...paramsState.filter,
                          'user.id': {
                            $in: value.map((a) => a.id),
                          },
                          'user.type': 'collector',
                        }),
                      );
                    } else {
                      setFilterCollectorPerson('');
                      const oldFilter = JSON.parse(
                        JSON.stringify(paramsState.filter),
                      );
                      delete oldFilter['user.id'];
                      delete oldFilter['user.type'];
                      dispatch(filterAdded(oldFilter));
                    }
                  }}
                  editable={
                    !paramsState?.filter?.['user.type'] ||
                    paramsState?.filter?.['user.type'] == 'collector'
                  }
                />
              </Layout.FilterItem>
            </>
          ) : (
            false
          )}
          <Layout.FilterItem>
            <CustomerSearch
              fields="_id name"
              testID="sales-activity-list-filter-customer"
              value={filterCustomer}
              multiple={true}
              customFilter={{
                status: 'approved',
              }}
              onChange={(value) => {
                setFilterCustomer(value);
                setFilterDeliveryAddress([]);

                const oldFilter = removeAddressFilter(paramsState.filter);
                delete oldFilter['customer.id'];

                if (value && value.length) {
                  dispatch(
                    filterAdded({
                      ...oldFilter,
                      'customer.id': { $in: value.map((a) => a.id) },
                    }),
                  );
                } else {
                  dispatch(filterAdded(oldFilter));
                }
              }}
            />
          </Layout.FilterItem>
          <Layout.FilterItem>
            <DeliveryAddressSearch
              testID="sales-activity-list-filter-delivery-address"
              value={filterDeliveryAddress}
              multiple={true}
              editable={
                Array.isArray(filterCustomer) &&
                filterCustomer.some((item) => item?.id && item.id !== 'all')
              }
              customFilter={{
                status: 'approved',
                _id: {
                  $in:
                    Array.isArray(filterCustomer) &&
                    filterCustomer.some((item) => item?.id && item.id !== 'all')
                      ? filterCustomer
                          .filter((item) => item?.id && item.id !== 'all')
                          .map((c) => c.id)
                      : [],
                },
              }}
              onChange={(value) => {
                const isCustomerSelected =
                  Array.isArray(filterCustomer) &&
                  filterCustomer.some((item) => item?.id && item.id !== 'all');

                const oldFilter = removeAddressFilter(paramsState.filter);

                if (!isCustomerSelected) {
                  setFilterDeliveryAddress([]);
                  dispatch(filterAdded(oldFilter));
                  return;
                }

                const selectedValue = Array.isArray(value) ? value : [];
                setFilterDeliveryAddress(selectedValue);

                const selectedAddresses = selectedValue.filter(
                  (item) => item?.id && item.id !== 'all',
                );

                if (!selectedAddresses.length) {
                  dispatch(filterAdded(oldFilter));
                  return;
                }

                const deliveryIds = selectedAddresses
                  .filter((item) => item?.address_type === 'delivery')
                  .map((item) => item?.ref_id)
                  .filter(Boolean);

                const storeIds = selectedAddresses
                  .filter((item) => item?.address_type === 'store')
                  .map((item) => item?.ref_id)
                  .filter(Boolean);

                const outletIds = selectedAddresses
                  .filter((item) => item?.address_type === 'outlet')
                  .map((item) => item?.ref_id)
                  .filter(Boolean);

                if (
                  deliveryIds.length &&
                  !storeIds.length &&
                  !outletIds.length
                ) {
                  dispatch(
                    filterAdded({
                      ...oldFilter,
                      'customer.address.id': { $in: deliveryIds },
                      'customer.address.type': 'delivery',
                    }),
                  );
                  return;
                }

                if (
                  storeIds.length &&
                  !deliveryIds.length &&
                  !outletIds.length
                ) {
                  dispatch(
                    filterAdded({
                      ...oldFilter,
                      'customer.address.id': { $in: storeIds },
                      'customer.address.type': 'store',
                    }),
                  );
                  return;
                }

                if (
                  outletIds.length &&
                  !deliveryIds.length &&
                  !storeIds.length
                ) {
                  dispatch(
                    filterAdded({
                      ...oldFilter,
                      'customer.address.id': { $in: outletIds },
                      'customer.address.type': 'outlet',
                    }),
                  );
                  return;
                }

                dispatch(
                  filterAdded({
                    ...oldFilter,
                    customer_address_delivery_ids: { $in: deliveryIds },
                    customer_address_store_ids: { $in: storeIds },
                    customer_address_outlet_ids: { $in: outletIds },
                  }),
                );
              }}
            />
          </Layout.FilterItem>
          <Layout.FilterItem lastItem>
            <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
              Date
            </SociollaText>
            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
              <DatePicker
                testID="sales-activity-list-filter-start-date"
                label="Start Date"
                name="visited_at"
                style={{ width: '49%' }}
                value={paramsState.filter.visited_at?.$gte}
                onChange={(value) => {
                  const val = {
                    ...paramsState.filter.visited_at,
                    $gte: value.toISOString(),
                  };
                  dispatch(
                    filterAdded({ ...paramsState.filter, visited_at: val }),
                  );
                }}
              />
              <DatePicker
                testID="sales-activity-list-filter-end-date"
                label="End Date"
                name="visited_at"
                style={{ width: '49%' }}
                value={paramsState.filter.visited_at?.$lte}
                disableBeforeDate={paramsState.filter.visited_at?.$gte}
                disableAfterDate={
                  page_type === 'past_activities' ? new Date() : undefined
                }
                onChange={(value) => {
                  const val = {
                    ...paramsState.filter.visited_at,
                    $lte: value.toISOString(),
                  };
                  dispatch(
                    filterAdded({ ...paramsState.filter, visited_at: val }),
                  );
                }}
              />
            </View>
          </Layout.FilterItem>
        </Layout.Filter>
      </Layout.Container>
      <SalesActivityDetail
        id={detailId}
        show={showDetail}
        route={route}
        navigation={navigation}
        onClose={(shouldRefresh) => handleDetail('', shouldRefresh)}
      />
      <CreateNewPlan
        show={showCreate}
        selectedRole={selectedRole}
        onClose={() => setShowCreate(false)}
        onSubmit={(val) => openForm(val)}
      />
      <SelectNewPlanRole
        show={showSelectRole}
        onClose={() => setShowSelectRole(false)}
        onSubmit={(val) => {
          setSelectedRole(val);
          setShowSelectRole(false);
          setShowCreate(true);
        }}
      />
      <SalesActivityForm
        activityType={activityType}
        show={showForm}
        selectedRole={selectedRole}
        onClose={() => setShowForm(false)}
        onSubmit={() => {
          setShowForm(false);
          if (page_type == 'planned_activities') {
            doFetchTotal();
          } else {
            doFetchTotal();
          }
        }}
      />
      <ModalFull
        testID="sales-activity-export-options-modal"
        title="Export Activity Options"
        noTranslate={true}
        show={exportActivityOption}
        maxWidth={isWeb && !isMobileWeb ? 500 : '100%'}
        onClose={() =>
          dispatch(showPopup({ name: 'exportActivityOption', value: false }))
        }>
        <View style={styles.userCreate}>
          <TouchableOpacity
            testID="sales-activity-export-past-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => pastActivity()}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              {'Export '}
              {props.route.params.type == 'overdue_activities'
                ? 'Overdue'
                : 'Past'}
              {' Activities'}
            </SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            testID="sales-activity-export-gwp-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => gwpTracker()}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              Export GWP Tracker
            </SociollaText>
          </TouchableOpacity>
        </View>
      </ModalFull>
      <ModalFull
        testID="sales-activity-export-modal"
        title={`Export ${
          props.route.params.type == 'overdue_activities' ? 'Overdue' : 'Past'
        } Activities`}
        noTranslate={true}
        show={showPastExport}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showPastExport',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup>
                <SalesTeamSearch
                  testID="sales-activity-list-export-sales-team"
                  label="Sales Team"
                  value={filterExport.sales_team}
                  multiple={true}
                  customFilter={SALESTEAM_FILTER}
                  isAll
                  onChange={(value) => {
                    setFilterExport({
                      ...filterExport,
                      sales_team: value,
                      sales_person: [],
                      beauty_advisor: [],
                      merchandiser: [],
                      promotor: [],
                      customer: [{ id: 'all', name: 'All' }],
                    });
                  }}
                />
              </Layout.FormGroup>
              {![
                'salesman',
                'beauty_advisor',
                'merchandiser',
                'promotor',
                'collector',
              ].includes(userRole) ? (
                <>
                  {!filterSalesTeamChanged ? (
                    <>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-export-sales-person"
                          label="Sales Person"
                          value={filterExport.sales_person}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER()}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              sales_person: value,
                              beauty_advisor: [],
                              merchandiser: [],
                              promotor: [],
                              collector: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.beauty_advisor?.length > 0 ||
                              filterExport.merchandiser?.length > 0 ||
                              filterExport.promotor?.length > 0 ||
                              filterExport.collector?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-export-beauty-advisor"
                          label="Beauty Advisor"
                          placeholder="Select Beauty Advisor"
                          value={filterExport.beauty_advisor}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('beauty_advisor')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              beauty_advisor: value,
                              sales_person: [],
                              merchandiser: [],
                              promotor: [],
                              collector: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.merchandiser?.length > 0 ||
                              filterExport.promotor?.length > 0 ||
                              filterExport.collector?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-export-merchandiser"
                          label="Merchandiser"
                          placeholder="Select Merchandiser"
                          value={filterExport.merchandiser}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('merchandiser')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              merchandiser: value,
                              beauty_advisor: [],
                              sales_person: [],
                              promotor: [],
                              collector: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.beauty_advisor?.length > 0 ||
                              filterExport.promotor?.length > 0 ||
                              filterExport.collector?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-export-promotor"
                          label="Promotor"
                          placeholder="Select Promotor"
                          value={filterExport.promotor}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('promotor')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              promotor: value,
                              beauty_advisor: [],
                              sales_person: [],
                              merchandiser: [],
                              collector: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.beauty_advisor?.length > 0 ||
                              filterExport.merchandiser?.length > 0 ||
                              filterExport.collector?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-export-collector"
                          label="Collector"
                          placeholder="Select Collector"
                          value={filterExport.collector}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('collector')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              collector: value,
                              beauty_advisor: [],
                              sales_person: [],
                              merchandiser: [],
                              promotor: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.beauty_advisor?.length > 0 ||
                              filterExport.merchandiser?.length > 0 ||
                              filterExport.promotor?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                    </>
                  ) : (
                    <Layout.FormGroup style={{ height: 64 }}>
                      <SociollaText>Loading...</SociollaText>
                    </Layout.FormGroup>
                  )}
                </>
              ) : (
                false
              )}
              {!filterSalesPersonChanged ? (
                <Layout.FormGroup>
                  <CustomerSearch
                    fields="_id name"
                    testID="sales-activity-list-export-customer"
                    value={filterExport.customer}
                    multiple={true}
                    customFilter={{
                      ...CUSTOMER_FILTER,
                    }}
                    isAll
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        customer: value,
                      });
                    }}
                  />
                </Layout.FormGroup>
              ) : (
                <Layout.FormGroup style={{ height: 64 }}>
                  <SociollaText>Loading...</SociollaText>
                </Layout.FormGroup>
              )}
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    testID="sales-activity-list-export-start-date"
                    label="Start Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$gte}
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $gte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                  <DatePicker
                    testID="sales-activity-list-export-end-date"
                    label="End Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$lte}
                    disableBeforeDate={filterExport.visited_at?.$gte}
                    disableAfterDate={
                      page_type === 'past_activities' ? new Date() : undefined
                    }
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $lte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    marginTop: 15,
                  }}>
                  <Button
                    testID="sales-activity-export-submit-btn"
                    rounded={true}
                    type="primary"
                    buttonStyle={{ width: 90 }}
                    disabledStyle={{ width: 90 }}
                    disabled={exporting}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        {exporting ? 'Loading...' : 'Export'}
                      </SociollaText>
                    }
                    onPress={() => exportPastActivity()}
                  />
                </View>
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
        </View>
      </ModalFull>
      <ModalFull
        testID="sales-activity-bulk-delete-modal"
        title="Delete Activity"
        show={showBulkDeleteActivity}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showBulkDeleteActivity',
              value: false,
            }),
          )
        }>
        <Layout.FormWrapper style={{ padding: 15 }}>
          <Layout.FormGroup>
            <SalesPersonSearch
              testID="sales-activity-list-bulk-delete-sales-person"
              label="Sales Person"
              value={filterDelete.user_ids}
              multiple={true}
              isAll
              customFilter={SALESPERSON_FILTER()}
              onChange={(value) => {
                setFilterDelete({
                  ...filterDelete,
                  user_ids: value,
                });
              }}
            />
          </Layout.FormGroup>
          <Layout.FormGroup>
            <CustomerSearch
              fields="_id name"
              testId="sales-activity-list-bulk-delete-customer"
              value={filterDelete.customer_ids}
              multiple={true}
              customFilter={{
                status: 'approved',
              }}
              isAll
              onChange={(value) => {
                setFilterDelete({
                  ...filterDelete,
                  customer_ids: value,
                });
              }}
            />
          </Layout.FormGroup>
          <Layout.FormGroup lastItem>
            <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
              Date
            </SociollaText>
            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
              <DatePicker
                testID="sales-activity-list-bulk-delete-start-date"
                label="Start Date"
                name="visited_at"
                style={{ width: '49%' }}
                value={filterDelete.visited_at?.$gte}
                onChange={(value) => {
                  const val = {
                    ...filterDelete.visited_at,
                    $gte: value.toISOString(),
                  };
                  setFilterDelete({
                    ...filterDelete,
                    visited_at: val,
                  });
                }}
              />
              <DatePicker
                testID="sales-activity-list-bulk-delete-end-date"
                label="End Date"
                name="visited_at"
                style={{ width: '49%' }}
                value={filterDelete.visited_at?.$lte}
                disableBeforeDate={filterDelete.visited_at?.$gte}
                onChange={(value) => {
                  const val = {
                    ...filterDelete.visited_at,
                    $lte: value.toISOString(),
                  };
                  setFilterDelete({
                    ...filterDelete,
                    visited_at: val,
                  });
                }}
              />
            </View>
          </Layout.FormGroup>
          <Layout.FormGroup>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
                marginTop: 15,
              }}>
              <Button
                testID="sales-activity-bulk-delete-submit-btn"
                rounded={true}
                type="primary"
                buttonStyle={{ width: 90 }}
                disabledStyle={{ width: 90 }}
                disabled={removing}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    {removing ? 'Loading...' : 'Delete'}
                  </SociollaText>
                }
                onPress={() => onBulkDelete()}
              />
            </View>
          </Layout.FormGroup>
        </Layout.FormWrapper>
      </ModalFull>
      <ModalFull
        testID="sales-activity-gwp-export-modal"
        title="Export GWP Tracker"
        noTranslate={true}
        show={showGWPTracker}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showGWPTracker',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup>
                <SalesTeamSearch
                  testID="sales-activity-list-gwp-export-sales-team"
                  label="Sales Team"
                  value={filterExport.sales_team}
                  multiple={true}
                  customFilter={SALESTEAM_FILTER}
                  isAll
                  onChange={(value) => {
                    setFilterExport({
                      ...filterExport,
                      sales_team: value,
                      beauty_advisor: [],
                      promotor: [],
                      customer: [{ id: 'all', name: 'All' }],
                    });
                  }}
                />
              </Layout.FormGroup>
              {!['beauty_advisor', 'promotor'].includes(userRole) ? (
                <>
                  {!filterSalesTeamChanged ? (
                    <>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          testID="sales-activity-list-gwp-export-beauty-advisor"
                          label="Beauty Advisor"
                          placeholder="Select Beauty Advisor"
                          value={filterExport.beauty_advisor}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('beauty_advisor')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              beauty_advisor: value,
                              promotor: [],
                            });
                          }}
                          editable={!(filterExport.promotor?.length > 0)}
                        />
                      </Layout.FormGroup>
                    </>
                  ) : (
                    <Layout.FormGroup style={{ height: 64 }}>
                      <SociollaText>Loading...</SociollaText>
                    </Layout.FormGroup>
                  )}
                </>
              ) : (
                false
              )}
              {!filterSalesPersonChanged ? (
                <Layout.FormGroup>
                  <CustomerSearch
                    fields="_id name"
                    testID="sales-activity-list-gwp-export-customer"
                    value={filterExport.customer}
                    multiple={true}
                    customFilter={{
                      ...CUSTOMER_FILTER,
                    }}
                    isAll
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        customer: value,
                      });
                    }}
                  />
                </Layout.FormGroup>
              ) : (
                <Layout.FormGroup style={{ height: 64 }}>
                  <SociollaText>Loading...</SociollaText>
                </Layout.FormGroup>
              )}
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    testID="sales-activity-list-gwp-export-start-date"
                    label="Start Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$gte}
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $gte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                  <DatePicker
                    testID="sales-activity-list-gwp-export-end-date"
                    label="End Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$lte}
                    disableBeforeDate={filterExport.visited_at?.$gte}
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $lte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    marginTop: 15,
                  }}>
                  <Button
                    testID="sales-activity-gwp-export-submit-btn"
                    rounded={true}
                    type="primary"
                    buttonStyle={{ width: 90 }}
                    disabledStyle={{ width: 90 }}
                    disabled={exporting}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        {exporting ? 'Loading...' : 'Export'}
                      </SociollaText>
                    }
                    onPress={() => exportGWPTracker()}
                  />
                </View>
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
        </View>
      </ModalFull>
    </>
  );
};

const styles = StyleSheet.create({
  cardTitle: { fontSize: 24, marginBottom: 10 },
  cardDesc: { fontSize: isWeb ? 18 : 16, color: Colors.grey1 },
  userCreate: {
    width: '100%',
    paddingHorizontal: isWeb ? 25 : 15,
    paddingTop: isWeb ? 25 : 15,
    paddingBottom: isWeb ? 40 : 15,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  itemOption: {
    padding: 15,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginHorizontal: isWeb ? 15 : 10,
  },
  itemText: {
    fontSize: 14,
    marginTop: 15,
  },
  activityUserTag: {
    fontSize: isWeb ? 13 : 11,
    color: '#5D6DB8',
    marginTop: 4,
  },
  activityCheckLabel: {
    fontSize: isWeb ? 11 : 10,
    color: Colors.grey1,
  },

  activityCheckTime: {
    fontSize: isWeb ? 12 : 11,
    color: Colors.grey1,
    marginTop: 3,
    textAlign: 'center',
  },
});

export default SalesActivityListScreen;
